import { API_BASE_URL } from "src/config/apiBase";
import { Plus, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { decryptToken } from '../utils/getToken';

const CompanyAssets = ({ candidate, onClose }) => {
  const [assets, setAssets] = useState([
    { assetName: '', assetType: '', description: '', value: '', assignDate: '' }
  ]);
  const [existingAssets, setExistingAssets] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editAssetId, setEditAssetId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAssetId, setDeleteAssetId] = useState(null);
  const apiUrl = API_BASE_URL;
  const token = decryptToken("accessToken") || ""

  useEffect(() => {
    if (candidate?.CandidateEmail) fetchAssets();
  }, [candidate]);

  const fetchAssets = async () => {
    try {
      const res = await fetch(`${apiUrl}/assets/${candidate.id}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (!Array.isArray(data.data)) return;
      const filtered = data.data.filter(
        (item) => item.candidateEmail === candidate.CandidateEmail
      );
      setExistingAssets(filtered);
    } catch (err) {
      console.error("Error fetching assets:", err);
    }
  };

  const addAsset = () => {
    setAssets([
      ...assets,
      { assetName: '', assetType: '', description: '', value: '', assignDate: '' }
    ]);
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...assets];
    updated[index][name] = value;
    setAssets(updated);
  };

  const handleEdit = (asset) => {
    setAssets([{
      assetName: asset.assetName,
      assetType: asset.assetType,
      description: asset.description,
      value: asset.value,
      assignDate: asset.assignDate || ''
    }]);
    setEditMode(true);
    setEditAssetId(asset.id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteAssetId) return;
    try {
      await fetch(`${apiUrl}/assets/${deleteAssetId}`, {
        method: 'DELETE',
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      toast.success("Asset deleted!");
      fetchAssets();
    } catch (err) {
      console.error("Failed to delete asset", err);
      toast.error("Failed to delete asset.");
    } finally {
      setDeleteAssetId(null); // close popup
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const url = editMode ? `${apiUrl}/assets/${editAssetId}` : `${apiUrl}/assets`;
      const method = editMode ? 'PUT' : 'POST';
      const payload = editMode
        ? { candidateEmail: candidate.CandidateEmail, candidateName: candidate.CandidateName,candidateId: candidate.id,...assets[0] }
        : { candidateEmail: candidate.CandidateEmail, candidateName: candidate.CandidateName,candidateId: candidate.id, assets };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success("Asset saved successfully!");
        setAssets([{ assetName: '', assetType: '', description: '', value: '', assignDate: '' }]);
        setEditMode(false);
        setEditAssetId(null);
        fetchAssets();
      } else toast.error("Failed to save asset.");
    } catch (err) {
      console.error("Error saving asset:", err);
      toast.error("Error saving asset.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-[99999] flex flex-col justify-end items-center">
      <div className="bg-white w-full max-h-[90vh] max-w-[600px] rounded-t-2xl shadow-xl p-5 overflow-y-auto transition-transform duration-300 animate-slide-up">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{editMode ? "Edit Asset" : "Assign Assets"} for {candidate.CandidateName}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-red-500 text-sm font-medium">
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          {assets.map((asset, idx) => (
            <div key={idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3">
              <div>
                <label className="block text-gray-600 mb-1">Asset Name</label>
                <input
                  name="assetName"
                  value={asset.assetName}
                  onChange={(e) => handleChange(idx, e)}
                  required
                  className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="Laptop, Phone, etc."
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Asset Type</label>
                <input
                  name="assetType"
                  value={asset.assetType}
                  onChange={(e) => handleChange(idx, e)}
                  required
                  className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="Electronics, Furniture..."
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Description</label>
                <input
                  name="description"
                  value={asset.description}
                  onChange={(e) => handleChange(idx, e)}
                  className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="Details about the asset"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Value</label>
                <input
                  type="number"
                  name="value"
                  value={asset.value}
                  onChange={(e) => handleChange(idx, e)}
                  className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="50000"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Assign Date</label>
                <input
                  type="date"
                  name="assignDate"
                  value={asset.assignDate}
                  onChange={(e) => handleChange(idx, e)}
                  required
                  className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-2 mt-5">
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {isLoading ? (editMode ? "Updating..." : "Saving...") : (editMode ? "Update Asset" : "Save Asset")}
            </button>
            <button
              type="button"
              onClick={addAsset}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 text-sm flex items-center justify-center gap-1"
            >
              <Plus size={16} /> Add Asset
            </button>
          </div>
        </form>

        <h3 className="text-lg font-semibold text-gray-800 mt-8">Existing Assets</h3>
        {existingAssets.length === 0 ? (
          <p className="text-gray-600">No assets assigned yet.</p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Asset Name</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">Value</th>
                  <th className="px-4 py-2 text-left">Assign Date</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {existingAssets.map((asset) => (
                  <tr key={asset.id} className="border-t">
                    <td className="px-4 py-2">{asset.assetName}</td>
                    <td className="px-4 py-2">{asset.assetType}</td>
                    <td className="px-4 py-2">{asset.description}</td>
                    <td className="px-4 py-2">{asset.value}</td>
                    <td className="px-4 py-2">{asset.assignDate ? new Date(asset.assignDate).toLocaleDateString('en-IN') : '-'}</td>
                    <td className="px-4 py-2 flex gap-2">
                      <button
                        onClick={() => handleEdit(asset)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteAssetId(asset.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {deleteAssetId && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-[999999] flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
              <p className="text-gray-600 mb-4">Are you sure you want to delete this asset?</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteAssetId(null)}
                  className="px-3 py-1.5 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyAssets;
